import PropTypes from 'prop-types';
import {observer, PropTypes as MobXPropTypes} from 'mobx-react';
import React from 'react';

import CollapsibleNumber from 'core/components/CollapsibleNumber.js';
import BookNowButton from 'core/components/BookNowButton';
import Link from 'core/components/Link.js';

import {
    BOOKINGS_APP_URL,
    HICAPS,
    TELEHEALTH,
    TOOLTIP_TELEHEALTH_TEXT,
    TOOLTIP_HICAPS_TEXT,
    DIRECTORY_SEARCH_RESULT_CARD_LINK,
    PROFILE_CARD,
    AVATAR_SIZE_DEFAULT,
    AVATAR_SIZE_LARGE,
    BOOK_NOW_BTN_MH1_PRIMARY
} from 'core/constants.js';

import myHealthFirst from 'bookings/my_health_first.js';
import autobind from 'common/decorators/autobind.js';
import {DirectoryStoreContext} from 'core/stores/RootStore.js';
import {openLinkInNewTab, browserCheckForIE11} from 'core/utils.js';

import {Card, Chip, Pill} from '@HealthShareAU/hs-component-library';

export default
@observer
class ProfileCard extends React.Component {
    static contextType = DirectoryStoreContext;
    static propTypes = {
        avatar: PropTypes.string,
        customBookingDataUrl: PropTypes.string,
        displayName: PropTypes.string.isRequired,
        hasIntegratedBookings: PropTypes.bool,
        hasFees: PropTypes.bool,
        healthFund: PropTypes.object,
        id: PropTypes.number.isRequired,
        initials: PropTypes.string.isRequired,
        interest: PropTypes.shape({
            id: PropTypes.number,
        }),
        interestsDescription: PropTypes.string,
        isEnhanced: PropTypes.bool.isRequired,
        isReferrals: PropTypes.bool.isRequired,
        offersHicaps: PropTypes.bool.isRequired,
        offersTelehealth: PropTypes.bool.isRequired,
        phone: PropTypes.string,
        practice: PropTypes.shape({
            address: PropTypes.string.isRequired,
            id: PropTypes.number.isRequired,
            localityName: PropTypes.string,
            localityState: PropTypes.string,
            name: PropTypes.string.isRequired,
            phones: MobXPropTypes.arrayOrObservableArrayOf(
                PropTypes.shape({
                    number: PropTypes.string.isRequired,
                }),
            ),
            practiceUrl: PropTypes.string,
        }).isRequired,
        profileUrl: PropTypes.string.isRequired,
        specialties: PropTypes.oneOfType([
            PropTypes.arrayOf(
                PropTypes.shape({
                    name: PropTypes.string.isRequired,
                }),
            ),
            PropTypes.object, // ObservableArray
        ]).isRequired,
        specialty: PropTypes.shape({
            isMedical: PropTypes.bool.isRequired,
        }).isRequired,
        ratings: PropTypes.shape({
            average: PropTypes.number.isRequired,
            count: PropTypes.number.isRequired,
        }),
    };

    constructor(props) {
        super(props);

        this.state = {
            modalDisplayed: false,
        };
        this.modalProps = {
            closeModal: this.closeModal,
            contactId: props.id,
            contactName: props.displayName,
            isEnhanced: props.isEnhanced,
            items: [props.practice].map((item) => {
                item.type = 'profile';
                return item;
            }),
            phoneNumber: props.phone,
            type: 'profile',
        };
    }

    @autobind
    closeModal() {
        this.setState({modalDisplayed: false});
    }

    @autobind
    handlePhoneClick() {
        this.setState({modalDisplayed: true});
    }

    get profileUrl() {
        const {healthFund, interest, profileUrl} = this.props;
        let url = profileUrl;

        const urlParams = {};
        if (healthFund?.id > 0) {
            urlParams['health_fund'] = healthFund.id;
        }
        if (interest?.id) {
            urlParams['special_interest'] = interest.id;
        }

        const queryParams = Object.entries(urlParams)
            .map(([k, v]) => `${k}=${v}`)
            .join('&');

        return `${url}${queryParams ? `?${queryParams}` : ''}`;
    }

    @autobind
    getSpecialtyName(specialty) {
        const {
            rootStore: {
                paramStore: {isProUser, isReferrals},
            },
        } = this.context;
        if (isProUser || isReferrals) {
            return specialty.professionalName;
        } else {
            return specialty.name;
        }
    }

    renderSpecialties() {
        const {specialties} = this.props;
        if (!specialties.length) {
            return null;
        }
        return (
            <>
                {specialties
                    .map((specialty) => {
                        return this.getSpecialtyName(specialty);
                    })
                    .join(', ')}
            </>
        );
    }

    renderSpecialInterests() {
        const {interestsDescription} = this.props;
        const {isMobileOrTablet} = this.context;
        if (!interestsDescription) {
            return null;
        }
        return (
            <Chip
                concatenationText=" "
                concatenationWidth={isMobileOrTablet ? 100 : 150}
                customClass={'chip-special-interests'}
                hasConcatenation
                icon="practitioner"
                key="chip-special-interests"
                text={interestsDescription}
                title="Special interests:"
            />
        );
    }

    renderPhoneNumber() {
        const {displayName, phone} = this.props;
        if (!phone) {
            return null;
        }
        return (
            <Chip
                customClass="chip-phone"
                icon="phone"
                key="chip-phone"
                linkComponent={
                    <CollapsibleNumber
                        customClass="expanded"
                        number={phone}
                        onClick={this.handlePhoneClick}
                        title={`Click to show phone number for ${displayName}`}
                    />
                }
            />
        );
    }

    renderAddress() {
        return (
            <Chip
                customClass="chip-address"
                icon="address"
                key="chip-address"
                text={this.props.practice.address}
            />
        );
    }

    renderFees() {
        const {hasFees, healthFund, id, specialty} = this.props;

        const iconColor = healthFund?.colorBrand
            ? {
                  color: `#${healthFund.colorBrand}`,
              }
            : null;

        if (hasFees && healthFund?.id > 0 && specialty.isMedical) {
            return (
                <Chip
                    customClass="chip-cost fees-upsell"
                    icon="feesGraph"
                    iconColor={iconColor}
                    key="chip-cost"
                    linkComponent={
                        <Link href={`${this.profileUrl}#fees`}>
                            {'See out-of-pocket costs'}
                        </Link>
                    }
                />
            );
        }
        return null;
    }

    getRatingAverage() {
        const {healthFund, ratings} = this.props;
        const {
            rootStore: {
                paramStore: {isReferrals},
            },
        } = this.context;
        if (
            isReferrals ||
            !ratings ||
            (healthFund?.id && !healthFund?.showRatings)
        ) {
            return;
        }

        const {average} = ratings;
        return average;
    }

    getRatingCount() {
        const {healthFund, ratings} = this.props;
        const {
            rootStore: {
                paramStore: {isReferrals},
            },
        } = this.context;
        if (
            isReferrals ||
            !ratings ||
            (healthFund?.id && !healthFund?.showRatings)
        ) {
            return;
        }

        const {count} = ratings;
        return count;
    }

    @autobind
    bookingsAction() {
        const {id} = this.props;
        window.location.assign(
            'href',
            `${BOOKINGS_APP_URL}?practitioner=${id}`,
        );
    }

    @autobind
    handleBookingAnchorAction(bookingId) {
        const element = document.getElementById(bookingId);
        if (element) {
            element.click();            
        }
    }


    @autobind
    appendAnchor(bookingId){
       const element = document.createElement('a')
       element.setAttribute('id', `${bookingId}`);
       element.setAttribute('class', '1stAvailableWidget2');
       element.setAttribute('data-mh1-source', 'healthshare');
       element.setAttribute('rel', 'nofollow noopener');
       document.body.appendChild(element);
    }

    renderBookingButton() {
        const {customBookingDataUrl, hasIntegratedBookings, id,bookingIntegration
        } = this.props;
        let mh1BookingIntegrationId = null

        for (const key in bookingIntegration){
            if(key.toLowerCase().includes("mh1")){
                mh1BookingIntegrationId = bookingIntegration[key]
            }
        }

        const {
            rootStore: {
                paramStore: {isReferrals},
            },
        } = this.context;

        if (!isReferrals) {
            // MH1 has the highest priority 
            if(mh1BookingIntegrationId){
                this.appendAnchor(mh1BookingIntegrationId)
                 myHealthFirst.widgetSetup();
                return (
                    <div className="hs-button-container">
                        <BookNowButton
                            action =  {()=>this.handleBookingAnchorAction(mh1BookingIntegrationId)}
                            gaObjectType={PROFILE_CARD}
                            gaSelector = {BOOK_NOW_BTN_MH1_PRIMARY}
                            objectId={id}
                            objectType="directory result"
                            params={{practitioner: id}}
                        />
                    </div>
                );
            }
            if (hasIntegratedBookings) {
                return (
                    <div className="hs-button-container">
                        <BookNowButton
                            gaObjectType={PROFILE_CARD}
                            objectId={id}
                            objectType="directory result"
                            params={{practitioner: id}}
                        />
                    </div>
                );
            }
            
            if (customBookingDataUrl) {
                return (
                    <div className="hs-button-container">
                        <BookNowButton
                            action={() =>
                                openLinkInNewTab(customBookingDataUrl)
                            }
                            gaObjectType={PROFILE_CARD}
                            objectId={id}
                            objectType="directory result"
                            params={{practitioner: id}}
                        />
                    </div>
                );
            }
        }
        return null;
    }

    render() {
        const {
            avatar,
            displayName,
            initials,
            isEnhanced,
            offersHicaps,
            offersTelehealth,
        } = this.props;
        const {modalDisplayed} = this.state;
        const {
            rootStore: {
                paramStore: {isStaff, isClient},
            },
            telehealthDirectory,
        } = this.context;

        const directoryCardProps = {
            avatarInitials: initials && initials,
            avatarSizeTablet: isClient
                ? AVATAR_SIZE_DEFAULT
                : AVATAR_SIZE_LARGE,
            avatarSource: avatar && avatar,
            bookingModule: !isClient && this.renderBookingButton(),
            chips: [
                this.renderSpecialInterests(),
                this.props.practice.address && this.renderAddress(),
                this.renderPhoneNumber(),
                this.renderFees(),
            ],
            dataTestId: 'profileCard',
            description: '',
            heading: displayName,
            isEnhanced: isStaff && isEnhanced,
            isFeatured: false,
            isIntegrated: isClient,
            pills: (telehealthDirectory ||
                offersTelehealth ||
                offersHicaps) && [
                offersTelehealth && (
                    <Pill
                        customClass={'telehealth-pill'}
                        key="telehealth"
                        text={TELEHEALTH}
                        tooltip={
                            browserCheckForIE11()
                                ? null
                                : TOOLTIP_TELEHEALTH_TEXT
                        }
                    />
                ),
                offersHicaps && (
                    <Pill
                        customClass={'hicaps-pill'}
                        key="hicaps"
                        text={HICAPS}
                        tooltip={
                            browserCheckForIE11() ? null : TOOLTIP_HICAPS_TEXT
                        }
                    />
                ),
            ],
            ratingAverage: this.getRatingAverage(),
            ratingCount: this.getRatingCount(),
            size: 'default',
            subHeading: this.renderSpecialties(),
        };

        return (
            <li
                className={`cell ${
                    telehealthDirectory ? 'telehealth-directory' : ''
                }`.trim()}
            >
                {modalDisplayed && this.context.renderModal(this.modalProps)}
                <Link
                    customClass="card-link"
                    gaObjectType={PROFILE_CARD}
                    gaSelector={DIRECTORY_SEARCH_RESULT_CARD_LINK}
                    href={this.profileUrl}
                >
                    <span>{`${displayName} profile link`}</span>
                </Link>
                <Card
                    {...directoryCardProps}
                    customClass={isStaff ? 'selectable' : ''}
                />
            </li>
        );
    }
}
